import React, {
  useState,
  useContext,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DataGrid,
  GridColDef,
  GridColumnVisibilityModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  LayoutContext,
  ILayoutContext,
  DialogModesEnum,
  ToastSeverityEnum,
  WrapFormView,
  DataLoader,
} from '@uderly/react-uderly-ui';
import { IndexedRow, ModuleHandle } from '@uderly/react-uderly-ui/models';
import { useUderlyStore } from '@uderly/react-uderly-ui/zustand';
import ModuleType from './Module/models/ModuleType';
import ModuleNavBar from './Module/ModuleNavBar';

import Settings from '../settings';
import { PagedResponse } from './Module/models/PagedResponse';
import ConnectionError from './Module/ConnectionError';
import { AxiosResponse } from 'axios';
import { Grid } from '@mui/material';
import {
  createItemRequest,
  deleteItemRequest,
  getItemRequest,
  getRecordsRequest,
  updateItemRequest,
} from '@uderly/react-uderly-ui/api';
import { format, addSeconds } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

// Props a module host should have
export interface ModuleHostProps {
  data: ModuleType;
}

export interface ModuleProps {
  columns: GridColDef<any>[];
  mobileColumnVisibilityModel?: GridColumnVisibilityModel;
  className?: string;
  view?:
  | ((item: any) => ReactNode)
  | ((item: any, props: FormikProps<any>) => ReactNode);
  viewClassName?: string;
  schema?: any;
  disableInsert?: boolean | undefined;
  disableEdit?: boolean | undefined;
  disableView?: boolean | undefined;
  disableDelete?: boolean | undefined;
  enableSelectionOnClick?: boolean | undefined;
  enableInlineDelete?: boolean | undefined;
  mockedRecords?: readonly any[]; // Temporary
  data?: ModuleType;
  initialValues?: any;
  initialFilters?: any;
  filtersForm?: ({ values }: FormikProps<any>) => JSX.Element | undefined;
  onSubmitted?: (response: AxiosResponse<any> | null) => void;
  onItemLoaded?: (item: any) => void;
  onInsert?: () => void;
  onDelete?: (AxiosResponse) => void;
  onCreateAdapter?: (values: any) => any;
  onUpdateAdapter?: (values: any) => any;
  onReordered?: (source: any, dest: any) => Promise<void> | void;
}

export type WrapFormViewProps = ModuleProps;

export enum ModuleStatus {
  showRecords,
  showItem,
  createItem,
  showError,
}

export const DisplayCreatedAt = (props: {
  createdAt: string;
  timezone?: string;
}) => {
  const { t } = useTranslation();

  const timezone = props.timezone ?? 'Europe/London';
  // For time zones where daylight savings time is applicable a Date should be passed on the
  // second parameter to ensure the offset correctly accounts for DST at that time of year.
  const timezoneOffset = getTimezoneOffset(timezone, new Date(props.createdAt)); // ms
  const dt = addSeconds(new Date(props.createdAt), timezoneOffset / 1000);

  return (
    <Grid item xs={12} className="display-created-at">
      <label>{t('created-on')}</label>
      <p>
        {format(dt, 'd/MM/Y H:mm')}
      </p>
    </Grid>
  );
};

const Module = React.forwardRef<ModuleHandle, ModuleProps>((props, ref) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;
  const user = useUderlyStore.getState().user;

  const { t } = useTranslation();
  const { id } = useParams();

  const moduleRef = useRef<HTMLDivElement>(null);

  const mobileBreakpoint = 800;

  useImperativeHandle(ref, () => ({
    updateRecord: updateRecord,
    createItem: onSubmit,
    fetchItem: fetchItem,
    updateItem: updateItem,
    setItem: setItem,
  }));

  const _defaultSettings = {
    pageSize: 25,
    pageSizeOptions: [15, 25, 50, 100]
  };

  const [boundingRect, setBoundingRect] = useState<DOMRect>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  } as DOMRect);

  const [error, setError] = useState<null | React.ReactNode>(null);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(_defaultSettings.pageSize);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);

  const isRefreshRequested = useRef<boolean | null>(false);

  // Retrieve props and set default values for not specified fields

  const _defaultProps: any = {
    initialValues: {},
  };

  const initialValues: any = Object.assign(
    {},
    _defaultProps,
    props.initialValues
  );

  // Retrieve ModuleType data and set default values for not specified fields

  const _defaultData: ModuleType = {
    name: '',
    apiBasePath: '/',
    title: 'Module',
    path: '',
    // eslint-disable-next-line react/jsx-no-useless-fragment
    icon: <></>,
    createPath: '/create',
  };

  const data: ModuleType = Object.assign({}, _defaultData, props.data);

  const {
    columns,
    mobileColumnVisibilityModel,
    className,
    enableInlineDelete,
    disableInsert,
    disableEdit,
    disableView,
    disableDelete
  } = props;
  const { filtersForm, onInsert, onDelete } = props;

  const defaultMobileColumnVisibilityModel = {
    id: false,
    created_at: false,
  };

  const navigate = useNavigate();

  const [sColumns, setColumns] = useState<GridColDef<any>[] | null>(null);
  const [status, setStatus] = useState(ModuleStatus.showRecords);
  const [records, setRecords] = useState<readonly any[] | null>(null);
  const [item, setItem] = useState(null);
  const [filters, setFilters] = useState(props.initialFilters ?? {});

  const navigateToItem = useCallback(
    (row: any) => {
      setItem(row);
      setStatus(ModuleStatus.showItem);

      if (data) navigate(data.path + '/' + row.id);
    },
    [data, navigate]
  );

  const fetchItem = useCallback(
    async (id: number) => {
      // If using mock data, find the record to display
      let item: any = null;

      if (props.mockedRecords) {
        const filtered = props.mockedRecords.filter((r) => r.id !== id);

        if (filtered && filtered[0]) {
          item = filtered[0];
          navigateToItem(filtered[0]);
        }
      } else {
        // Fetch item information from the server
        const response = await getItemRequest(`${Settings.apiUrl}${data.apiBasePath}/${id}`);

        if (response && response.status === 200) {
          item = response.data.data;
          navigateToItem(response.data.data);
        }
      }

      if (props.onItemLoaded) props.onItemLoaded(item);

      return;
    },
    [data.apiBasePath, navigateToItem, props]
  );

  const fetchRecordsAbortController = useRef<AbortController | null>(null);

  const fetchRecords = useCallback(
    async (newFilters: any = {}) => {
      setLoading(true);

      const _defaultFilters = {
        page: 1,
        page_size: pageSize,
      };

      const updatedFilters = Object.assign(
        {},
        _defaultFilters,
        filters,
        newFilters
      );

      // Only fetch the records if filters are different or a refresh has been requested
      if (
        isRefreshRequested.current ||
        JSON.stringify(updatedFilters) !== JSON.stringify(filters)
      ) {
        //setStatus(ModuleStatus.loading);
        isRefreshRequested.current = false;

        if (fetchRecordsAbortController.current !== null)
          fetchRecordsAbortController.current.abort();

        fetchRecordsAbortController.current = new AbortController();

        const url = Settings.apiUrl + data.apiBasePath;
        const response = await getRecordsRequest(
          url,
          updatedFilters,
          fetchRecordsAbortController.current
        );
        console.log(url, response, updatedFilters)
        fetchRecordsAbortController.current = null;

        setFilters(updatedFilters);

        if (response && response.status === 200) {
          const pagedResponse: PagedResponse<any> = response.data;

          setRecords(pagedResponse.data);
          setRowCount(
            pagedResponse.records_count === null
              ? pagedResponse.data.length
              : pagedResponse.records_count
          );
          setPage(
            pagedResponse.current_page ? pagedResponse.current_page - 1 : 0
          );
          setStatus(ModuleStatus.showRecords);
        } else {
          if (response && (response as unknown as string) !== 'canceled') {
            setError(<ConnectionError />);
            setStatus(ModuleStatus.showError);
          }
        }
      }

      setLoading(false);
    },
    [data.apiBasePath, filters, pageSize]
  );

  const onInsertCallback = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
        | undefined = undefined
    ) => {
      console.log('onInsert, path: ', data.path + data.createPath);

      setItem(initialValues);
      setStatus(ModuleStatus.createItem);

      isRefreshRequested.current = true;

      if (onInsert) onInsert();

      if (data) navigate(data.path + data.createPath);
    },
    [data, initialValues, navigate, onInsert]
  );

  const backToRecords = useCallback(() => {
    if (!records) fetchRecords();

    setStatus(ModuleStatus.showRecords);
    setItem(null);

    if (data) navigate(data.path);
  }, [data, fetchRecords, navigate, records]);

  const onDeleteCallback = useCallback(
    async (item: any) => {
      console.log('User has confirmed to delete the item', item);

      // props.backdrop.show();

      const url = Settings.apiUrl + data.apiBasePath + '/' + item.id;

      const response = await deleteItemRequest(url);

      if (onDelete) onDelete(response);

      if (response && (response.status === 200 || response.status === 204)) {
        isRefreshRequested.current = true;

        // props.backdrop.hide();
        backToRecords();
      } else {
        // Error
      }
    },
    [backToRecords, data.apiBasePath, id /*, props.backdrop*/]
  );

  const confirmDelete = useCallback(
    (item: any) => {
      layoutContext.dialog.show({
        title: t('delete-confirmation'),
        content: t('delete-confirmation-desc'),
        mode: DialogModesEnum.confirmation,
        onConfirm: () => onDeleteCallback(item),
      });
    },
    [layoutContext.dialog, onDelete, t]
  );

  const RowMenu = useCallback(
    (props: any) =>
      enableInlineDelete ? (
        <IconButton
          aria-label="delete"
          onClick={(e) => {
            e.stopPropagation();
            confirmDelete(props.row);
          }}
        >
          <DeleteIcon />
        </IconButton>
      ) : null,
    [confirmDelete, enableInlineDelete]
  );

  const hasUserPermission = (postfix: string, prefix?: string) => {
    if (user && user.roles && prefix) {
      const { roles } = user;

      for (const role of roles) {
        if (role.permissions) {
          const hasPermission = role.permissions.filter(p => p.title === `${prefix}_${postfix}`).length > 0;

          if (hasPermission)
            return true;
        }
      }
    }

    return false;
  }

  const canDelete = useRef<boolean>(!disableDelete &&
    (!data.permissionPrefix ? true : hasUserPermission("delete", data.permissionPrefix)));
  const canEdit = useRef<boolean>(!disableEdit &&
    (!data.permissionPrefix ? true : hasUserPermission("edit", data.permissionPrefix)));
  const canCreate = useRef<boolean>(!disableInsert &&
    (!data.permissionPrefix ? true : hasUserPermission("create", data.permissionPrefix)));

  useEffect(() => {
    const defaultColSettings = {
      filterable: false,
      hideable: false,
      sortable: false,
    };

    // Build the final columns array, starting off default column settings
    if (!sColumns) {
      const cols = columns.map((c: any) => {
        return { ...defaultColSettings, ...c };
      });

      if (enableInlineDelete)
        cols.push({
          field: 'actions',
          headerName: '',
          sortable: false,
          filterable: false,
          minWidth: 30,
          renderCell: (params: any) => <RowMenu row={params.row} />,
        });

      setColumns(cols);
    }

    if (id) {
      if (!item) {
        // Item Mode (Create or View/Edit)
        if ('/' + id === data.createPath) {
          console.log('CREATE PATH', id);
          onInsertCallback();
        } else {
          fetchItem(parseInt(id));
        }
      }
    } else {
      // Records Mode
      if (props.mockedRecords) setRecords(props.mockedRecords);
      else fetchRecords();
    }

    window.onpopstate = () => {
      if (item) {
        setStatus(ModuleStatus.showRecords);
        setItem(null);
      }
    };

    const handleResize = () => {
      if (moduleRef.current) {
        const r = moduleRef.current.getBoundingClientRect();

        if (
          boundingRect.x !== r.x ||
          boundingRect.y !== r.y ||
          boundingRect.width !== r.width ||
          boundingRect.height !== r.height
        )
          setBoundingRect(r);
      }
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [
    RowMenu,
    boundingRect.height,
    boundingRect.width,
    boundingRect.x,
    boundingRect.y,
    columns,
    data.createPath,
    enableInlineDelete,
    fetchItem,
    fetchRecords,
    id,
    item,
    onInsertCallback,
    props.mockedRecords,
    sColumns,
  ]);

  const onRowClick = (e: GridRowParams) => {
    const { row } = e;

    if (!disableView)
      fetchItem(row.id as number);
  };

  const defaultOnSubmitted = (response: AxiosResponse<any> | null) => {
    if (response) {
      const { status } = response;

      if (status === 201) {
        layoutContext.toast.show({
          open: true,
          content: t('item-created'),
          severity: ToastSeverityEnum.success,
          autoHideDuration: 5000,
        });

        return;
      }

      if (status === 202) {
        layoutContext.toast.show({
          open: true,
          content: t('item-updated'),
          severity: ToastSeverityEnum.success,
          autoHideDuration: 5000,
        });

        return;
      }

      if (status === 200 || status === 202) {
        return;
      }
    }

    layoutContext.toast.show({
      open: true,
      content: t('server-error'),
      severity: ToastSeverityEnum.error,
      autoHideDuration: 5000,
    });
  };

  const updateRecord = (row: IndexedRow) => {
    if (row && row.id >= 0 && records) {
      const i = records.findIndex((item) => item.id === row.id);

      if (i >= 0) {
        const updatedRecords = [...records];
        updatedRecords[i] = row;
        setRecords(updatedRecords);
      }
    }
  };

  const updateItem = async (item: any) => {
    let response: null | AxiosResponse<any> = null;

    if (item.id) {
      const url = Settings.apiUrl + data.apiBasePath + '/' + id;

      delete item['createdBy'];
      delete item['createdOn'];

      console.log('onSubmit', item);

      const adaptedItem = props.onUpdateAdapter
        ? props.onUpdateAdapter(item)
        : item;

      response = (await updateItemRequest(
        url,
        adaptedItem
      )) as AxiosResponse<any>;

      updateRecord(response.data.data as IndexedRow);
    } else {
      const url = Settings.apiUrl + data.apiBasePath;
      const adaptedItem = props.onCreateAdapter
        ? props.onCreateAdapter(item)
        : item;

      console.log('Update', adaptedItem);
      response = (await createItemRequest(
        url,
        adaptedItem
      )) as AxiosResponse<any>;

      if (response.status === 201) {
        fetchItem(response.data.data.id);
      }
    }

    return response;
  };

  const onSubmit = async (item: any) => {
    // props.backdrop.show();
    console.log(item);
    const response: null | AxiosResponse<any> = await updateItem(item);

    if (props.onSubmitted) props.onSubmitted(response);
    else defaultOnSubmitted(response);

    // props.backdrop.hide();
    return response;
  };

  const onFiltersChanged = async (values: any) => {
    setFilters(values);

    fetchRecords({ ...values, page: 1 });
  };

  const handleSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel.length > 0) {
        const f: any = {
          ...filters,
          sort: sortModel[0].field,
          dir: sortModel[0].sort,
        };

        fetchRecords(f);
      }
    },
    [fetchRecords, filters]
  );

  const onDragEnd = async (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (records) {
      const items = reorder(
        records as [],
        result.source.index,
        result.destination.index
      );

      setRecords(items);

      if (props.onReordered)
        await props.onReordered(records[result.source.index], records[result.destination.index]);
    }
  }

  const reorder = (list: [], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed as never);

    return result;
  };

  return (
    <div className={'module ' + (className ?? '')} ref={moduleRef}>
      {loading &&
        <DataLoader fullscreen overlay />}

      {status === ModuleStatus.showError && error}

      {status === ModuleStatus.showRecords && (
        <>
          <ModuleNavBar
            data={data}
            onInsert={!canCreate.current ? undefined : onInsertCallback}
            filtersForm={filtersForm}
            filtersInitialValues={filters}
            onFiltersChanged={onFiltersChanged} />

          {/* TODO: Manage empty records message? */}

          {!data.enableReorder && sColumns && records && (
            <DataGrid
              className="datagrid"
              rows={records}
              columns={sColumns}
              columnVisibilityModel={
                boundingRect.width < mobileBreakpoint
                  ? mobileColumnVisibilityModel
                    ? mobileColumnVisibilityModel
                    : defaultMobileColumnVisibilityModel
                  : {}
              }
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: pageSize,
                    page: page,
                  },
                },
              }}
              pageSizeOptions={_defaultSettings.pageSizeOptions}
              disableRowSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={rowCount}
              onPaginationModelChange={(model, _details) => {
                setPageSize(model.pageSize);
                fetchRecords({
                  page: model.page + 1,
                  page_size: model.pageSize,
                });
              }}
              sortingMode="server"
              onSortModelChange={handleSortModelChange}
              loading={loading}
              // disableSelectionOnClick
              disableColumnFilter
              onRowClick={onRowClick}
              sx={{ width: boundingRect.width, height: 100 }}
            />
          )}

          {data.enableReorder && sColumns && records && canEdit &&
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <table className={`datagrid ${snapshot.isDraggingOver ? 'is-dragging' : ''}`}>
                    <thead>
                      <tr>
                        {sColumns.map((col, _index) => (
                          <th style={{ flex: col.flex, minWidth: col.minWidth, width: col.width }}>
                            {(col.headerName && col.headerName !== "") ? col.headerName : <>&nbsp;</>}</th>))}
                      </tr>
                    </thead>

                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                      {records.map((item, index) => (
                        <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`draggable ${snapshot.isDragging ? "is-dragging" : ""}`}
                              onClick={() => { if (onRowClick) onRowClick({ id: item.id, columns: sColumns, row: item }) }}
                              style={provided.draggableProps.style}>
                              {sColumns.map(col =>
                                <td key={`qm-item-${col.field}-${item.id}`} style={{ flex: col.flex, minWidth: col.minWidth, width: col.width }}>
                                  {col.renderCell ? col.renderCell({ row: item } as any) : item[col.field]}</td>)}
                            </tr>
                          )}
                        </Draggable>
                      ))}
                    </tbody>
                  </table>)}
              </Droppable>
            </DragDropContext>
          }
        </>)}

      {(status === ModuleStatus.showItem ||
        status === ModuleStatus.createItem) &&
        props.view && (
          <WrapFormView
            {...props}
            item={item as any}
            onBack={backToRecords}
            onDelete={onDeleteCallback}
            disableDelete={!canDelete.current}
            disableUpdate={!canEdit}
            onSubmit={async (item: any) => {
              await onSubmit(item);
            }}
          />
        )}
    </div>
  );
});

export default Module;
